import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Plus,
  Trash2,
  Check,
  Loader2,
  ShieldCheck,
  CalendarDays,
  CreditCard,
  Landmark,
  CheckCircle2,
  Backpack,
} from "lucide-react";
import { CAMP_DATES, SCHOOL_YEARS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FULL_DAYS = 4;
const ADDON = 10;
const YOUNG_YEARS = ["Year 1", "Year 2"];

const addonCost = (child) =>
  (child.early_dropoff ? ADDON : 0) + (child.late_collection ? ADDON : 0);

const costFor = (child, rates) =>
  (child.days.length >= FULL_DAYS ? rates.fourDay : child.days.length * rates.day) +
  addonCost(child);

const emptyChild = () => ({
  name: "",
  days: [],
  school_year: "",
  medical_info: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  emergency_contact_email: "",
  early_dropoff: false,
  late_collection: false,
});

const field =
  "w-full rounded-2xl border-2 border-slate-200 bg-slate-50 px-4 py-3 font-semibold text-slate-800 outline-none focus:border-sky-400 focus:bg-white transition-colors";
const labelCls = "block text-sm font-bold text-slate-500 mb-1.5";

export const Booking = () => {
  const [parentName, setParentName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [children, setChildren] = useState([emptyChild()]);
  const [method, setMethod] = useState("card"); // card | childcare
  const [scheme, setScheme] = useState("");
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [rates, setRates] = useState({ day: 40, fourDay: 144, isEarlyBird: true });

  useEffect(() => {
    axios
      .get(`${API}/config/childcare`)
      .then(({ data }) => {
        setSchemes(data);
        if (data[0]) setScheme(data[0].id);
      })
      .catch(() => {});
    axios
      .get(`${API}/config/pricing`)
      .then(({ data }) =>
        setRates({
          day: data.day_rate / 100,
          fourDay: data.four_day_rate / 100,
          isEarlyBird: data.is_early_bird,
        })
      )
      .catch(() => {});
  }, []);

  const updateChild = (idx, patch) =>
    setChildren((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

  const toggleDay = (idx, dateId) =>
    setChildren((prev) =>
      prev.map((c, i) => {
        if (i !== idx) return c;
        const has = c.days.includes(dateId);
        return {
          ...c,
          days: has ? c.days.filter((d) => d !== dateId) : [...c.days, dateId],
        };
      })
    );

  const addChild = () => setChildren((prev) => [...prev, emptyChild()]);
  const removeChild = (idx) =>
    setChildren((prev) => prev.filter((_, i) => i !== idx));

  const total = useMemo(
    () => children.reduce((sum, c) => sum + costFor(c, rates), 0),
    [children, rates]
  );
  const booked = children.filter((c) => c.days.length > 0);

  const validate = () => {
    if (!parentName.trim() || !parentEmail.trim()) {
      setError("Please add your name and email so we can confirm the booking.");
      return false;
    }
    if (booked.length === 0) {
      setError("Please select at least one day for at least one child.");
      return false;
    }
    for (const c of booked) {
      const who = c.name.trim() || "each child";
      if (!c.name.trim()) {
        setError("Please add every child's name.");
        return false;
      }
      if (!c.school_year) {
        setError(`Please choose a school year for ${who}.`);
        return false;
      }
      if (!c.medical_info.trim()) {
        setError(
          `Please add medical information for ${who} — write "None" if there is nothing we should know.`
        );
        return false;
      }
      if (
        !c.emergency_contact_name.trim() ||
        !c.emergency_contact_phone.trim() ||
        !c.emergency_contact_email.trim()
      ) {
        setError(
          `Please add an emergency contact name, phone and email for ${who}.`
        );
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    setError("");
    if (!validate()) return;
    setLoading(true);
    const childrenPayload = booked.map((c) => ({
      name: c.name.trim(),
      days: c.days,
      school_year: c.school_year,
      medical_info: c.medical_info.trim(),
      emergency_contact_name: c.emergency_contact_name.trim(),
      emergency_contact_phone: c.emergency_contact_phone.trim(),
      emergency_contact_email: c.emergency_contact_email.trim(),
      early_dropoff: !!c.early_dropoff,
      late_collection: !!c.late_collection,
    }));
    try {
      if (method === "card") {
        const { data } = await axios.post(`${API}/payments/checkout`, {
          parent_name: parentName.trim(),
          parent_email: parentEmail.trim(),
          origin_url: window.location.origin,
          children: childrenPayload,
        });
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
        setError("Something went wrong starting checkout. Please try again.");
      } else {
        const { data } = await axios.post(`${API}/payments/childcare`, {
          parent_name: parentName.trim(),
          parent_email: parentEmail.trim(),
          scheme,
          children: childrenPayload,
        });
        setResult(data);
      }
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Sorry, we could not complete that. Please try again or get in touch."
      );
    }
    setLoading(false);
  };

  return (
    <section
      id="booking"
      className="relative bg-slate-50 py-24 md:py-36"
      data-testid="booking-section"
    >
      <div className="pointer-events-none absolute top-20 -left-24 h-96 w-96 rounded-full bg-sky-200/50 blur-3xl" />
      <div className="mx-auto max-w-6xl px-5 relative z-10">
        <div className="max-w-3xl">
          <span className="inline-block font-display font-bold text-sky-500 uppercase tracking-widest text-sm mb-4">
            Build Your Booking
          </span>
          <h2 className="font-display font-black tracking-tight text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Choose your days, <br />
            <span className="text-sky-500">book in a few taps.</span>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-slate-600 font-medium">
            Add each child, pick their days and tell us a little about them. Book
            all four days for £{rates.fourDay}, or £{rates.day} per day
            {rates.isEarlyBird ? " (early-bird price)" : ""}.
          </p>
        </div>

        <AnimatePresence mode="wait">
          {result ? (
            <ChildcareResult key="result" result={result} parentEmail={parentEmail} />
          ) : (
            <motion.div
              key="form"
              exit={{ opacity: 0 }}
              className="mt-12 grid lg:grid-cols-12 gap-6"
            >
              {/* Builder */}
              <div className="lg:col-span-8 space-y-6">
                {/* Parent details */}
                <div className="rounded-[2rem] bg-white border-2 border-slate-900/5 p-6 md:p-8 shadow-[0_20px_50px_rgba(15,23,42,0.06)]">
                  <h3 className="font-display font-extrabold text-xl text-slate-900 mb-5">
                    Your details
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Parent / guardian name</label>
                      <input
                        type="text"
                        value={parentName}
                        onChange={(e) => setParentName(e.target.value)}
                        data-testid="parent-name-input"
                        placeholder="e.g. Jane Smith"
                        className={field}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Your email address</label>
                      <input
                        type="email"
                        value={parentEmail}
                        onChange={(e) => setParentEmail(e.target.value)}
                        data-testid="parent-email-input"
                        placeholder="you@email.com"
                        className={field}
                      />
                    </div>
                  </div>
                </div>

                {/* Children */}
                {children.map((child, idx) => (
                  <motion.div
                    key={idx}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid={`child-card-${idx}`}
                    className="rounded-[2rem] bg-white border-2 border-slate-900/5 p-6 md:p-8 shadow-[0_20px_50px_rgba(15,23,42,0.06)]"
                  >
                    <div className="flex items-center justify-between mb-5 gap-4">
                      <h3 className="font-display font-extrabold text-xl text-slate-900">
                        Child {idx + 1}
                      </h3>
                      {children.length > 1 && (
                        <button
                          onClick={() => removeChild(idx)}
                          data-testid={`remove-child-${idx}`}
                          className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" /> Remove
                        </button>
                      )}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>Child&apos;s name</label>
                        <input
                          type="text"
                          value={child.name}
                          onChange={(e) => updateChild(idx, { name: e.target.value })}
                          data-testid={`child-name-input-${idx}`}
                          placeholder="e.g. Alex"
                          className={field}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>School year</label>
                        <select
                          value={child.school_year}
                          onChange={(e) =>
                            updateChild(idx, { school_year: e.target.value })
                          }
                          data-testid={`child-year-select-${idx}`}
                          className={field}
                        >
                          <option value="">Select year…</option>
                          {SCHOOL_YEARS.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className={labelCls}>
                        Medical information{" "}
                        <span className="font-medium text-slate-400">
                          (allergies, conditions, medication — write
                          &ldquo;None&rdquo; if none)
                        </span>
                      </label>
                      <textarea
                        value={child.medical_info}
                        onChange={(e) =>
                          updateChild(idx, { medical_info: e.target.value })
                        }
                        data-testid={`child-medical-input-${idx}`}
                        rows={2}
                        placeholder="Anything our coaches should be aware of"
                        className={field}
                      />
                    </div>

                    <div className="mt-4 rounded-2xl bg-slate-50 border border-slate-100 p-4">
                      <p className="text-sm font-bold text-slate-500 mb-3">
                        Emergency contact
                      </p>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <input
                          type="text"
                          value={child.emergency_contact_name}
                          onChange={(e) =>
                            updateChild(idx, {
                              emergency_contact_name: e.target.value,
                            })
                          }
                          data-testid={`child-ec-name-${idx}`}
                          placeholder="Contact name"
                          className={field}
                        />
                        <input
                          type="tel"
                          value={child.emergency_contact_phone}
                          onChange={(e) =>
                            updateChild(idx, {
                              emergency_contact_phone: e.target.value,
                            })
                          }
                          data-testid={`child-ec-phone-${idx}`}
                          placeholder="Contact phone"
                          className={field}
                        />
                        <input
                          type="email"
                          value={child.emergency_contact_email}
                          onChange={(e) =>
                            updateChild(idx, {
                              emergency_contact_email: e.target.value,
                            })
                          }
                          data-testid={`child-ec-email-${idx}`}
                          placeholder="Contact email"
                          className={field}
                        />
                      </div>
                    </div>

                    {/* What to bring */}
                    <div
                      className="mt-4 rounded-2xl bg-sky-50 border border-sky-100 p-4"
                      data-testid={`bring-list-${idx}`}
                    >
                      <p className="text-sm font-bold text-slate-700 mb-2 inline-flex items-center gap-2">
                        <Backpack className="h-4 w-4 text-sky-500" />
                        Please bring with you
                      </p>
                      <ul className="text-sm font-medium text-slate-600 space-y-1 list-disc pl-5">
                        {YOUNG_YEARS.includes(child.school_year) ? (
                          <>
                            <li>A change of clothes</li>
                            <li>Wellies</li>
                            <li>A coat or boiler suit</li>
                          </>
                        ) : child.school_year ? (
                          <>
                            <li>
                              Their own sports equipment — hockey stick, gum
                              shield, shin pads etc.
                            </li>
                            <li>Suitable footwear to run around in</li>
                          </>
                        ) : null}
                        <li>A nut-free packed lunch</li>
                        <li>A water bottle</li>
                      </ul>
                      {!child.school_year && (
                        <p className="mt-2 text-xs font-semibold text-slate-400">
                          Choose a school year above to see the full kit list
                          for {child.name.trim() || "this child"}.
                        </p>
                      )}
                      <p className="mt-2 text-xs font-bold text-red-500">
                        We are strictly nut-free — please no nuts or nut
                        products.
                      </p>
                    </div>

                    <p className="text-sm font-bold text-slate-500 mt-5 mb-2.5">
                      Select days
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {CAMP_DATES.map((d) => {
                        const selected = child.days.includes(d.date);
                        return (
                          <button
                            key={d.date}
                            onClick={() => toggleDay(idx, d.date)}
                            data-testid={`day-toggle-${idx}-${d.date}`}
                            className={`relative rounded-2xl border-2 p-4 text-left transition-all ${
                              selected
                                ? "border-sky-400 bg-sky-50 shadow-[0_8px_24px_rgba(56,189,248,0.25)]"
                                : "border-slate-200 bg-white hover:border-sky-200"
                            }`}
                          >
                            <span
                              className={`absolute top-3 right-3 grid h-5 w-5 place-items-center rounded-full transition-colors ${
                                selected
                                  ? "bg-sky-400 text-white"
                                  : "bg-slate-100 text-transparent"
                              }`}
                            >
                              <Check className="h-3 w-3" strokeWidth={3} />
                            </span>
                            <CalendarDays
                              className={`h-5 w-5 mb-2 ${
                                selected ? "text-sky-500" : "text-slate-400"
                              }`}
                            />
                            <p className="font-display font-extrabold text-slate-900 leading-none">
                              {d.day} {d.date}
                            </p>
                            <p className="text-xs font-bold text-slate-400 mt-1">
                              {d.month}
                            </p>
                          </button>
                        );
                      })}
                    </div>

                    <p className="text-sm font-bold text-slate-500 mt-5 mb-2.5">
                      Extended hours{" "}
                      <span className="font-medium text-slate-400">
                        (optional, £10 each per child)
                      </span>
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          updateChild(idx, { early_dropoff: !child.early_dropoff })
                        }
                        data-testid={`addon-early-${idx}`}
                        className={`relative rounded-2xl border-2 p-4 text-left transition-all ${
                          child.early_dropoff
                            ? "border-sky-400 bg-sky-50"
                            : "border-slate-200 bg-white hover:border-sky-200"
                        }`}
                      >
                        <span
                          className={`absolute top-3 right-3 grid h-5 w-5 place-items-center rounded-full ${
                            child.early_dropoff
                              ? "bg-sky-400 text-white"
                              : "bg-slate-100 text-transparent"
                          }`}
                        >
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </span>
                        <p className="font-display font-extrabold text-slate-900 leading-tight">
                          Early drop-off
                        </p>
                        <p className="text-xs font-bold text-slate-400 mt-1">
                          8.00–8.30am · +£10
                        </p>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          updateChild(idx, {
                            late_collection: !child.late_collection,
                          })
                        }
                        data-testid={`addon-late-${idx}`}
                        className={`relative rounded-2xl border-2 p-4 text-left transition-all ${
                          child.late_collection
                            ? "border-sky-400 bg-sky-50"
                            : "border-slate-200 bg-white hover:border-sky-200"
                        }`}
                      >
                        <span
                          className={`absolute top-3 right-3 grid h-5 w-5 place-items-center rounded-full ${
                            child.late_collection
                              ? "bg-sky-400 text-white"
                              : "bg-slate-100 text-transparent"
                          }`}
                        >
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </span>
                        <p className="font-display font-extrabold text-slate-900 leading-tight">
                          Late collection
                        </p>
                        <p className="text-xs font-bold text-slate-400 mt-1">
                          5.00–5.30pm · +£10
                        </p>
                      </button>
                    </div>
                    <p className="mt-2 text-xs font-semibold text-slate-400">
                      These are separate — early drop-off does not include late
                      collection, and vice versa. Regular hours are 8.30am–5.00pm.
                    </p>

                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-500">
                        {child.days.length === 4
                          ? "Full 4-day camp"
                          : `${child.days.length} day${
                              child.days.length === 1 ? "" : "s"
                            } selected`}
                      </span>
                      <span className="font-display font-extrabold text-sky-600">
                        £{costFor(child, rates)}
                      </span>
                    </div>
                  </motion.div>
                ))}

                <button
                  onClick={addChild}
                  data-testid="add-child-button"
                  className="w-full rounded-[2rem] border-2 border-dashed border-slate-300 py-5 font-display font-bold text-slate-500 hover:border-sky-400 hover:text-sky-600 hover:bg-sky-50/50 transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Plus className="h-5 w-5" /> Add another child
                </button>
              </div>

              {/* Summary */}
              <div className="lg:col-span-4">
                <div className="lg:sticky lg:top-28 rounded-[2rem] bg-slate-900 p-8 shadow-xl text-white">
                  <h3 className="font-display font-extrabold text-xl mb-5">
                    Your booking
                  </h3>
                  <div className="space-y-3 mb-5">
                    {booked.length === 0 && (
                      <p className="text-slate-400 font-medium text-sm">
                        Select days above to see your total.
                      </p>
                    )}
                    {booked.map((c, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="font-semibold text-slate-200">
                          {c.name.trim() || "Child"}{" "}
                          <span className="text-slate-500">
                            · {c.days.length === 4 ? "4-day" : `${c.days.length}d`}
                          </span>
                        </span>
                        <span className="font-bold">£{costFor(c, rates)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-white/10 pt-5 flex items-end justify-between">
                    <span className="font-display font-bold text-slate-300">
                      Total
                    </span>
                    <span
                      className="font-display font-black text-4xl text-sky-400 leading-none"
                      data-testid="booking-total"
                    >
                      £{total}
                    </span>
                  </div>

                  {/* Payment method */}
                  <p className="text-sm font-bold text-slate-400 mt-6 mb-3">
                    How would you like to pay?
                  </p>
                  <div className="space-y-2">
                    <button
                      onClick={() => setMethod("card")}
                      data-testid="method-card"
                      className={`w-full flex items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-colors ${
                        method === "card"
                          ? "border-sky-400 bg-white/10"
                          : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      <CreditCard className="h-5 w-5 text-sky-400" />
                      <span className="font-bold text-sm">Pay now by card</span>
                    </button>
                    <button
                      onClick={() => setMethod("childcare")}
                      data-testid="method-childcare"
                      className={`w-full flex items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-colors ${
                        method === "childcare"
                          ? "border-sky-400 bg-white/10"
                          : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      <Landmark className="h-5 w-5 text-sky-400" />
                      <span className="font-bold text-sm">
                        Tax-Free Childcare / Vouchers
                      </span>
                    </button>
                  </div>

                  {method === "childcare" && (
                    <div className="mt-3">
                      <select
                        value={scheme}
                        onChange={(e) => setScheme(e.target.value)}
                        data-testid="scheme-select"
                        className="w-full rounded-2xl border-2 border-white/10 bg-slate-800 px-4 py-3 font-semibold text-white outline-none focus:border-sky-400"
                      >
                        {schemes.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      <p className="mt-2 text-xs text-slate-400">
                        We&apos;ll reserve your place and email you our payment
                        reference and instructions.
                      </p>
                    </div>
                  )}

                  {error && (
                    <p
                      data-testid="booking-error"
                      className="mt-5 text-sm font-semibold text-red-300 bg-red-500/10 rounded-xl px-4 py-3"
                    >
                      {error}
                    </p>
                  )}

                  <motion.button
                    whileHover={{ scale: loading ? 1 : 1.03 }}
                    whileTap={{ scale: loading ? 1 : 0.97 }}
                    onClick={handleSubmit}
                    disabled={loading}
                    data-testid="checkout-button"
                    className="mt-6 w-full rounded-full bg-sky-400 py-4 font-display font-bold text-slate-900 text-lg shadow-[0_12px_30px_rgba(56,189,248,0.4)] hover:bg-sky-300 transition-colors disabled:opacity-70 inline-flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" /> Processing…
                      </>
                    ) : method === "card" ? (
                      <>Pay Securely · £{total}</>
                    ) : (
                      <>Reserve Place · £{total}</>
                    )}
                  </motion.button>

                  <p className="mt-4 flex items-center justify-center gap-2 text-xs font-semibold text-slate-400">
                    <ShieldCheck className="h-4 w-4 text-sky-400" />
                    {method === "card"
                      ? "Secure payment powered by Square"
                      : "Place reserved · pay via your childcare scheme"}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const ChildcareResult = ({ result, parentEmail }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    data-testid="childcare-result"
    className="mt-12 max-w-2xl rounded-[2rem] bg-white border-2 border-slate-900/5 p-8 md:p-10 shadow-[0_20px_50px_rgba(15,23,42,0.08)]"
  >
    <CheckCircle2 className="h-14 w-14 text-sky-500" />
    <h3 className="mt-5 font-display font-black text-3xl text-slate-900">
      Place reserved!
    </h3>
    <p className="mt-3 text-slate-600 font-medium">
      Your booking reference is{" "}
      <span className="font-display font-extrabold text-slate-900">
        {result.booking_ref}
      </span>
      . We&apos;ve emailed the details to {parentEmail || "you"}.
    </p>

    <div className="mt-6 rounded-2xl bg-sky-50 border border-sky-100 p-5">
      <p className="font-bold text-slate-900">
        Pay £{(result.amount / 100).toFixed(0)} via {result.scheme_label}
      </p>
      <p className="mt-2 text-slate-600 text-sm font-medium">{result.note}</p>
      <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white border border-sky-200 px-4 py-2">
        <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
          Reference
        </span>
        <span className="font-display font-extrabold text-slate-900">
          {result.reference}
        </span>
      </div>
    </div>

    <p className="mt-6 text-sm text-slate-500">
      Your place is held while payment is arranged. Any questions? Just get in
      touch — we&apos;re happy to help.
    </p>
  </motion.div>
);

export default Booking;
